import { useMemo, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import { EmptyState, ErrorState } from '../State/State'
import { SkeletonRows } from '../Skeleton/Skeleton'
import styles from './Table.module.scss'

export interface Column<Row> {
  key: string
  header: ReactNode
  render: (row: Row, index: number) => ReactNode
  align?: 'left' | 'right' | 'center'
  width?: number | string
  nowrap?: boolean
  /** Enables client-side sorting; return a comparable value. */
  sortValue?: (row: Row) => string | number | null | undefined
}

export interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[] | undefined
  rowKey: (row: Row, index: number) => string | number
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  emptyTitle?: string
  emptyDescription?: ReactNode
  emptyAction?: ReactNode
  onRowClick?: (row: Row) => void
  compact?: boolean
  hover?: boolean
  className?: string
  skeletonRows?: number
  /** Initial sort. */
  defaultSort?: { key: string; direction: 'asc' | 'desc' }
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  onRetry,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  onRowClick,
  compact,
  hover = true,
  className,
  skeletonRows = 6,
  defaultSort,
}: DataTableProps<Row>) {
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(defaultSort ?? null)

  const sorted = useMemo(() => {
    if (!rows || !sort) return rows ?? []
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sortValue) return rows
    const getter = col.sortValue
    const dir = sort.direction === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const va = getter(a)
      const vb = getter(b)
      if (va == null && vb == null) return 0
      if (va == null) return 1
      if (vb == null) return -1
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir
    })
  }, [rows, sort, columns])

  function toggleSort(col: Column<Row>) {
    if (!col.sortValue) return
    setSort((prev) => {
      if (prev?.key !== col.key) return { key: col.key, direction: 'asc' }
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' }
      return null
    })
  }

  const colSpan = columns.length
  let body: ReactNode
  if (loading) {
    body = (
      <tr>
        <td colSpan={colSpan} className={styles.stateCell}>
          <div style={{ padding: 14 }}>
            <SkeletonRows rows={skeletonRows} height={compact ? 34 : 44} />
          </div>
        </td>
      </tr>
    )
  } else if (error) {
    body = (
      <tr>
        <td colSpan={colSpan} className={styles.stateCell}>
          <ErrorState error={error} onRetry={onRetry} />
        </td>
      </tr>
    )
  } else if (!sorted.length) {
    body = (
      <tr>
        <td colSpan={colSpan} className={styles.stateCell}>
          <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
        </td>
      </tr>
    )
  } else {
    body = sorted.map((row, i) => (
      <tr key={rowKey(row, i)} onClick={onRowClick ? () => onRowClick(row) : undefined}>
        {columns.map((col) => (
          <td
            key={col.key}
            className={clsx(col.align === 'right' && styles.right, col.align === 'center' && styles.center, col.nowrap && styles.nowrap)}
            style={{ width: col.width }}
          >
            {col.render(row, i)}
          </td>
        ))}
      </tr>
    ))
  }

  return (
    <div className={clsx(styles.wrap, className)}>
      <table className={clsx(styles.table, hover && styles.hover, onRowClick && styles.clickable, compact && styles.compact)}>
        <thead>
          <tr>
            {columns.map((col) => {
              const active = sort?.key === col.key
              return (
                <th
                  key={col.key}
                  scope="col"
                  style={{ width: col.width }}
                  className={clsx(
                    col.align === 'right' && styles.right,
                    col.align === 'center' && styles.center,
                    col.sortValue && styles.sortable,
                  )}
                  onClick={() => toggleSort(col)}
                  aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {col.header}
                  {col.sortValue && active && (
                    <span className={styles.sortIcon}>
                      {sort?.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  )
}

export interface PaginationProps {
  page: number // 1-based
  pageSize: number
  total: number
  onChange: (page: number) => void
  className?: string
}

export function Pagination({ page, pageSize, total, onChange, className }: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const pages = useMemo(() => {
    const set = new Set<number>([1, pageCount, page, page - 1, page + 1])
    return [...set].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b)
  }, [page, pageCount])
  if (total <= 0) return null
  const from = (page - 1) * pageSize + 1
  const to = Math.min(total, page * pageSize)

  return (
    <nav className={clsx(styles.pagination, className)} aria-label="Pagination">
      <span>
        Showing {from}–{to} of {total}
      </span>
      <div className={styles.pages}>
        <button type="button" className={styles.pageBtn} onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label="Previous page">
          <ChevronLeft size={14} />
        </button>
        {pages.map((p, i) => (
          <span key={p} style={{ display: 'contents' }}>
            {i > 0 && pages[i - 1] !== p - 1 && <span className={styles.ellipsis}>…</span>}
            <button
              type="button"
              className={clsx(styles.pageBtn, p === page && styles.pageActive)}
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          </span>
        ))}
        <button
          type="button"
          className={styles.pageBtn}
          onClick={() => onChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </nav>
  )
}
